import { cn } from '@/lib/cn';
import type { HintResult } from '@/types';
import type { BetSide } from '../lib/side';
import { LaneLetter } from './CompareSummary';

/**
 * Which rules fire on each bet — the direct answer to "what does the bad bet
 * trip that the good one doesn't". Rules that fire on only one side are flagged;
 * a rule firing on B but not A is usually the finding.
 */

const SEV_ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };
const SEV_CLS: Record<string, string> = {
  critical: 'border-crit/40 bg-crit-bg text-crit',
  warning: 'border-warn/40 bg-warn-bg text-warn',
  info: 'border-info/40 bg-info-bg text-info',
};

export function CompareHints({ a, b }: { a: BetSide; b: BetSide }) {
  const mapA = index(a.result.hints);
  const mapB = index(b.result.hints);
  const ruleIds = [...new Set([...mapA.keys(), ...mapB.keys()])].sort((x, y) => {
    const sx = SEV_ORDER[(mapA.get(x) ?? mapB.get(x))!.severity] ?? 9;
    const sy = SEV_ORDER[(mapA.get(y) ?? mapB.get(y))!.severity] ?? 9;
    return sx - sy || x.localeCompare(y);
  });

  const onlyB = ruleIds.filter((id) => mapB.has(id) && !mapA.has(id)).length;
  const onlyA = ruleIds.filter((id) => mapA.has(id) && !mapB.has(id)).length;

  if (ruleIds.length === 0) {
    return (
      <section className="border-b border-hairline">
        <SectionHead label="Hints" note="neither bet fired a rule" tone="ok" />
      </section>
    );
  }

  return (
    <section className="border-b border-hairline">
      <SectionHead
        label="Hints"
        note={
          onlyA + onlyB === 0
            ? 'both bets fire the same rules'
            : [onlyB ? `B fires ${onlyB} that A does not` : '', onlyA ? `A fires ${onlyA} that B does not` : '']
                .filter(Boolean)
                .join(' · ')
        }
        tone={onlyA + onlyB === 0 ? 'muted' : 'warn'}
      />

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="border-b border-hairline">
              <th className="w-[150px] px-4 py-2 text-left eyebrow">rule</th>
              <ColHead letter="A" />
              <ColHead letter="B" />
            </tr>
          </thead>
          <tbody>
            {ruleIds.map((id) => {
              const ha = mapA.get(id) ?? null;
              const hb = mapB.get(id) ?? null;
              const flagged = Boolean(ha) !== Boolean(hb);
              return (
                <tr key={id} className={cn('border-b border-hairline/60', flagged && 'bg-warn-bg/25')}>
                  <td className="px-4 py-1.5 align-top">
                    <code className="font-mono text-[11px] text-text-dim">{id}</code>
                  </td>
                  <HintCell hint={ha} />
                  <HintCell hint={hb} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function index(hints: HintResult[]): Map<string, HintResult> {
  return new Map(hints.map((h) => [h.ruleId, h]));
}

function ColHead({ letter }: { letter: 'A' | 'B' }) {
  return (
    <th className="px-4 py-2 text-left">
      <LaneLetter letter={letter} />
    </th>
  );
}

function HintCell({ hint }: { hint: HintResult | null }) {
  if (!hint) return <td className="px-4 py-1.5 align-top text-text-faint">—</td>;
  if (hint.error) {
    return (
      <td className="px-4 py-1.5 align-top">
        <span className="font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-faint">
          rule error
        </span>
      </td>
    );
  }
  return (
    <td className="px-4 py-1.5 align-top">
      <div className="flex flex-col gap-1">
        <span
          className={cn(
            'inline-block w-fit border px-1.5 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider',
            SEV_CLS[hint.severity] ?? SEV_CLS.info,
          )}
        >
          {hint.severity}
        </span>
        <span className="text-[12px] leading-snug text-text">{hint.message}</span>
      </div>
    </td>
  );
}

function SectionHead({
  label,
  note,
  tone,
}: {
  label: string;
  note: string;
  tone: 'ok' | 'warn' | 'muted';
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
        {label}
      </span>
      <span
        className={cn(
          'font-mono text-[11px]',
          tone === 'ok' && 'text-ok',
          tone === 'warn' && 'text-warn',
          tone === 'muted' && 'text-text-dim',
        )}
      >
        {note}
      </span>
    </div>
  );
}
